<template>
  <n-modal
    v-model:show="showModal"
    preset="card"
    :title="mandatory ? 'Terms of Service (Required)' : 'Terms of Service'"
    class="max-w-2xl mx-4"
    style="margin: auto;width: 96%;"
    :closable="!mandatory"
    :mask-closable="!mandatory"
    :close-on-esc="!mandatory"
    :auto-focus="false"
    :trap-focus="false"
  >
    <div class="max-h-96 overflow-y-auto" ref="agreementContent" @scroll="handleScroll">
      <div v-if="loading" class="flex justify-center items-center py-12">
        <n-spin size="large">
          <template #description>
            <span class="text-gray-600">Loading agreement content...</span>
          </template>
        </n-spin>
      </div>
      
      <div v-else-if="agreement" class="prose prose-sm max-w-none">
        <div class="mb-2">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ agreement.title }}</h3>
          <p class="text-sm text-gray-600">Version: {{ agreement.version }}</p>
        </div>
        
        <div class="whitespace-pre-wrap text-gray-700" v-html="agreement.content"></div>
        
        <!-- 滚动提示 -->
        <div v-if="!hasScrolledToBottom" class="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p class="text-sm text-yellow-800 flex items-center gap-2">
            <n-icon size="16">
              <ArrowDown />
            </n-icon>
            {{ mandatory ? 'You must scroll to the bottom to read the complete agreement before proceeding' : 'Please scroll to the bottom to read the complete agreement' }}
          </p>
        </div>
      </div>
      
      <div v-else class="text-center py-12">
        <p class="text-gray-600">Failed to load agreement content</p>
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-between items-center">
        <div class="text-sm text-gray-600 text-sm">
          <input type="checkbox" name="agree">
          <span v-if="!hasScrolledToBottom" class="text-red-600">
            {{ mandatory ? 'You must read the complete agreement to continue registration' : 'Please read the complete agreement before confirming' }}
          </span>
          <span v-else>
             Complete agreement has been read
          </span>
        </div>
        
        <div class="flex gap-3">
          <n-button class="btn-full-gree"
            type="primary" 
            :disabled="!hasScrolledToBottom"
            @click="handleConfirm"
          >
            {{ mandatory ? 'Accept and Continue' : 'I have read and agree' }}
          </n-button>
        </div>
      </div>
    </template>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { NModal, NButton, NSpin, NIcon } from 'naive-ui'
import { ArrowDown } from '@vicons/ionicons5'
import type { UserAgreement } from '@/stores/auth'

interface Props {
  show: boolean
  agreement: UserAgreement | null
  loading?: boolean
  mandatory?: boolean // 是否为强制模式
}

interface Emits {
  (e: 'update:show', value: boolean): void
  (e: 'agreement-read'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  mandatory: false
})

const emit = defineEmits<Emits>()

const agreementContent = ref<HTMLElement>()
const hasScrolledToBottom = ref(false)

const showModal = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value)
})

// 监听模态框显示状态，重置滚动状态
watch(() => props.show, (newShow) => {
  if (newShow) {
    hasScrolledToBottom.value = false
    nextTick(() => {
      checkIfScrolledToBottom()
    })
  }
})

/**
 * 处理滚动事件
 */
const handleScroll = () => {
  checkIfScrolledToBottom()
}

/**
 * 检查是否滚动到底部
 */
const checkIfScrolledToBottom = () => {
  if (!agreementContent.value) return
  
  const element = agreementContent.value
  const threshold = 10 // 允许10px的误差
  
  const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold
  
  if (isAtBottom && !hasScrolledToBottom.value) {
    hasScrolledToBottom.value = true
  }
}

/**
 * 处理确认按钮点击
 */
const handleConfirm = () => {
  if (hasScrolledToBottom.value) {
    emit('agreement-read')
  }
}

/**
 * 处理关闭按钮点击
 */
const handleClose = () => {
  // 强制模式下不允许关闭
  if (!props.mandatory) {
    showModal.value = false
  }
}
</script>

<style scoped>
/* 自定义滚动条样式 */
.max-h-96::-webkit-scrollbar {
  width: 6px;
}

.max-h-96::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 协议内容样式 */
.prose h1, .prose h2, .prose h3, .prose h4 {
  color: #1f2937;
  font-weight: 600;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.prose h1 {
  font-size: 1.5em;
}

.prose h2 {
  font-size: 1.3em;
}

.prose h3 {
  font-size: 1.1em;
}

.prose p {
  margin-bottom: 1em;
  line-height: 1.6;
}

.prose ul, .prose ol {
  margin: 1em 0;
  padding-left: 1.5em;
}

.prose li {
  margin-bottom: 0.5em;
}

.prose strong {
  font-weight: 600;
  color: #1f2937;
}
</style>